/**
 * Today, as the device's local civil date, as an `IsoDate`.
 *
 * That's the device's local date, not a zone-pinned one. For this app's
 * users — everyone on the ACTAS roster, in ACT — the two coincide, which is
 * why this is fine for choosing which month to show. Contrast
 * `hasShiftStarted` in `src/engine/civil.ts`, which pins `Australia/Sydney`
 * explicitly via `Intl`: the undo guard it backs needs the real instant, not
 * "whatever this device's clock and timezone setting say today is", so it
 * can't rely on device-local the way this function does.
 *
 * `src/engine/` and `src/rules/` must stay pure — no clock, no DOM, no I/O
 * (CLAUDE.md's "roster engine" section; enforced for real code by
 * `src/__tests__/boundaries.test.ts`) — so nothing in there may call `new
 * Date()` for "now". This is the one place in the client layer that does,
 * kept to a single function so every screen depending on "today"
 * (`CalendarScreen`, `MonthGrid`, and whatever comes after them) takes it as
 * a prop instead of reaching for the clock itself, and stays deterministic
 * and testable as a result.
 *
 * Built from local date components rather than `new Date().toISOString()`,
 * which reads the date in UTC and so rolls back to the previous day for
 * anyone east of Greenwich before 10am — every user of this app, being in
 * Australia. Quarantined the same way, and for the same reason, as
 * `~/claudeCode/OTcalculator/src/app/settings.ts`'s `todayIso()`.
 */

import { toIsoDate } from '../engine/civil'
import type { IsoDate } from '../engine/types'

export function todayIso(): IsoDate {
  const now = new Date()
  return toIsoDate({
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    day: now.getDate(),
  })
}
